import { Avatar, Grid, Skeleton, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { observer } from "mobx-react";
import { permissionOptions, UserWithFullName } from "./constants";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import { sxClsx } from "utils/styleClsx";
import { PermissionSetter } from "./PermissionSetter";
import { useStore } from "utils/useStore";
import { useCurrentDashboard } from "../../../useCurrentDashboard";
import { IDashboardUserPermission } from "components/Signals/SignalsModels";
import { AuthStore } from "stores/AuthStore";

// Component to list and manage individual user permissions for the dashboard
export const UserPermissionsList = ({
    userPermissions,
    setUserPermissions,
    users,
    usersIsLoading = false,
    isCurrentUserOwner,
}: {
    userPermissions: IDashboardUserPermission[];
    setUserPermissions: (permissions: IDashboardUserPermission[]) => void;
    users: UserWithFullName[] | null | undefined;
    usersIsLoading?: boolean;
    isCurrentUserOwner: boolean;
}) => {
    const currentDashboard = useCurrentDashboard();

    const updateSingleUserPermission = async (
        userId: string,
        newPermission: ReportAccessType,
    ) => {
        if (!currentDashboard) return;
        await currentDashboard.updateDashboardUserPermissions({
            userIds: [userId],
            permissionToSet: newPermission,
        });
        const updatedPermissions = userPermissions.map((permission) =>
            permission.userId === userId
                ? { ...permission, accessType: newPermission }
                : permission,
        );
        setUserPermissions(updatedPermissions);
    };

    const removePermission = async (userId: string) => {
        if (!currentDashboard) return;
        const removed = await currentDashboard.removeUserPermission(userId);
        if (removed) {
            setUserPermissions(
                userPermissions.filter(
                    (permission) => permission.userId !== userId,
                ),
            );
        }
    };

    const theme = useTheme();

    if (usersIsLoading) {
        // Show a skeleton for 3 users as a placeholder
        return (
            <Grid
                sx={{
                    maxHeight: "278px",
                    overflowY: "auto",
                    borderTop: `1px solid ${theme.palette.gray[200]}`,
                    borderBottom: `1px solid ${theme.palette.gray[200]}`,
                }}
            >
                {[1, 2, 3].map((i) => (
                    <Grid
                        key={i}
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            py: 1,
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Grid
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 1,
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                            />
                            <Grid
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Skeleton
                                    variant="text"
                                    width={100}
                                    height={20}
                                />
                                <Skeleton
                                    variant="text"
                                    width={140}
                                    height={16}
                                />
                            </Grid>
                        </Grid>
                        <Skeleton
                            variant="rectangular"
                            width={90}
                            height={32}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (!users || users.length === 0) {
        return null;
    }

    return (
        <Grid
            sx={{
                maxHeight: "278px",
                overflowY: "auto",
                borderTop: `1px solid ${theme.palette.gray[200]}`,
                borderBottom: `1px solid ${theme.palette.gray[200]}`,
            }}
        >
            {userPermissions.map((permission) => {
                const user = users?.find((u) => u.userId === permission.userId);
                if (!user) return null;
                return (
                    <SingleUserPermission
                        key={permission.userId}
                        userPermission={permission}
                        onPermissionChange={updateSingleUserPermission}
                        user={user}
                        removePermission={removePermission}
                        isCurrentUserOwner={isCurrentUserOwner}
                    />
                );
            })}
        </Grid>
    );
};

const SingleUserPermission = observer(({
    userPermission,
    onPermissionChange,
    user,
    removePermission,
    isCurrentUserOwner,
}: {
    userPermission: IDashboardUserPermission;
    onPermissionChange: (
        userId: string,
        newPermission: ReportAccessType,
    ) => void;
    user: UserWithFullName;
    removePermission: (userId: string) => void;
    isCurrentUserOwner: boolean;
}) => {
    const theme = useTheme();
    const [accessType, setAccessType] = useState<ReportAccessType>(
        userPermission.accessType,
    );

    const authStore = useStore(AuthStore);

    if (user.userId === undefined) {
        console.error("User ID is undefined");
        return null;
    }

    // "You" for the "can't change your own access" rule is the real acting user
    // — the admin when impersonating, never the impersonated user. This row is
    // never editable. (AUT-10497)
    const isActingSelf = user.userId === authStore.actualUserId;
    // While impersonating, the impersonated user is the dashboard's original
    // owner. For a departed-owner handoff we allow removing them, but not role
    // changes (the backend still rejects changing the impersonated user's role),
    // so this row gets a remove-only control. (AUT-10497)
    const isImpersonatedOwner =
        authStore.isImpersonating &&
        user.userId === authStore.effectiveUserId;
    const canManageRow = isCurrentUserOwner && !isActingSelf;

    return (
        <Grid
            sx={sxClsx({
                display: "flex",
                flexDirection: "row",
                gap: 1,
                py: 1,
                justifyContent: "space-between",
                alignItems: "center",
            })}
        >
            <Grid sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                <Avatar
                    sx={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: theme.palette.primary[100],
                        color: theme.palette.primary.main,
                    }}
                >
                    {user.fullName.charAt(0)}
                </Avatar>
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Typography
                        sx={{
                            color: theme.palette.gray[700],
                            fontSize: "14px",
                            fontWeight: theme.typography.fontWeightSemibold,
                        }}
                    >
                        {user.fullName}
                    </Typography>
                    <Typography
                        sx={{
                            color: theme.palette.gray[500],
                            fontSize: "12px",
                        }}
                    >
                        {user.email}
                    </Typography>
                </Grid>
            </Grid>
            {canManageRow ? (
                <PermissionSetter
                    id={`permission-setter-${user.userName}`}
                    permissionToSet={accessType}
                    setPermissionToSet={setAccessType}
                    onPermissionSet={(newPermission) => {
                        onPermissionChange(user.userId!, newPermission);
                    }}
                    removeOption={true}
                    roleChangeDisabled={isImpersonatedOwner}
                    handleRemove={() => {
                        removePermission(user.userId!);
                    }}
                />
            ) : (
                <Typography sx={{ color: theme.palette.gray[700] }}>
                    {
                        permissionOptions.find(
                            (option) =>
                                option.value === userPermission.accessType,
                        )?.label
                    }
                </Typography>
            )}
        </Grid>
    );
});
